import React, {useEffect, useState} from "react";
import {useDispatch, useSelector} from "react-redux";
import {Link} from 'react-router-dom';
import {
  Row,
  Col,
  Spinner,
  Popover,
  PopoverBody,
  Accordion,
  AccordionBody,
  AccordionHeader,
  AccordionItem
} from 'reactstrap';
import CardItem from "../../components/PurpleTheme/CardItem";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import DocsTooltip from "../../components/Common/DocsTooltip";
import {deployRunbookScript, getLambdas, getRunbookScripts} from "../../store/daemon/actions";
import {convertS3UriToUrl} from "../../helpers/component_helpers";
import TableHeaders from "../../components/Daemon/TableHeaders";
import DeployDetailsModal from "./DeployDetailsModal";
import AddRunbookScriptModal from "./AddRunbookScriptModal";

const LambdaDetails = () => {
  const dispatch = useDispatch();
  const [masterLambdaLinkPopover, setMasterLambdaLinkPopover] = useState({});
  const [lambdaToolTip, setLambdaToolTip] = useState(false);
  const [deployingLambdaLoader, setDeployingLambdaLoader] = useState([]);
  const [updatingLambdaLoader, setUpdatingLambdaLoader] = useState([]);

  const [masterLambdaID, setMasterLambdaID] = useState(null);
  const [roleArn, setRoleArn] = useState(null);
  const [lambdaArn, setLambdaArn] = useState(null);
  const [deployDetailsModalOpen, setDeployDetailsModalOpen] = useState(false);
  const [deployDetailsModalSubmitted, setDeployDetailsModalSubmitted] = useState(false);
  const [deployDetailsModalForUpdate, setDeployDetailsModalForUpdate] = useState(false);

  const [deployedLambdaArnPopover, setDeployedLambdaArnPopover] = useState({});
  const [deployedLambdaRoleArnPopover, setDeployedLambdaRoleArnPopover] = useState({});
  const [selectedScript, setSelectedScript] = useState(null);
  const [scriptModalOpen, setScriptModalOpen] = useState(false);
  const [openAccount, setOpenAccount] = useState(null);

  const toggleAccordion = (id) => {
    setOpenAccount(openAccount === id ? null : id);
  }

  const masterLambdaHeaders = [
    {name: "Lambda Name", width: 4},
    {name: "Description", width: 6},
    {name: "Actions", width: 2}
  ]

  const deployedLambdaHeaders = [
    {name: "Lambda Name", width: 3},
    {name: "Function ARN", width: 3},
    {name: "Role ARN", width: 3},
    {name: "Actions", width: 3}
  ]

  const {master_lambdas, deployed_lambdas, runbook_scripts} = useSelector(state => ({
    master_lambdas: state.Daemon.master_lambdas,
    deployed_lambdas: state.Daemon.deployed_lambdas,
    runbook_scripts: state.Daemon.runbook_scripts || []
  }));

  const deployed_lambdas_grouped = deployed_lambdas

  useEffect(() => {
    dispatch(getLambdas())
    dispatch(getRunbookScripts())
  }, []);


  useEffect(() => {
    if (master_lambdas != null) {
      setDeployingLambdaLoader(new Array(master_lambdas.length).fill(false))
    }
  }, [master_lambdas])

  useEffect(() => {
    if (deployed_lambdas != null) {
      setUpdatingLambdaLoader(new Array(deployed_lambdas.length).fill(false))
    }
  }, [deployed_lambdas]);

  function toggleScriptUrlTooltip(master_lambda_id, isOpen) {
    setMasterLambdaLinkPopover(prevState => ({
      ...prevState,
      [master_lambda_id]: isOpen,
    }));
  }

  function toggleDeployedLambdaArnTooltip(deployed_lambda_id, isOpen) {
    setDeployedLambdaArnPopover(prevState => ({
      ...prevState,
      [deployed_lambda_id]: isOpen,
    }));
  }

  function toggleDeployedLambdaRoleArnTooltip(deployed_lambda_id, isOpen) {
    setDeployedLambdaRoleArnPopover(prevState => ({
      ...prevState,
      [deployed_lambda_id]: isOpen,
    }));
  }


  const handleDeployScript = (scriptId) => {
    dispatch(deployRunbookScript({id: scriptId}));
  };


  function toggleDeployDetailsModal() {
    if (deployDetailsModalOpen && !deployDetailsModalSubmitted) {
      if (deployDetailsModalForUpdate) {
        setUpdatingLambdaLoader((prevArray) =>
          prevArray.map((value, i) => (masterLambdaID && deployed_lambdas[i]?.master_lambda_id === masterLambdaID ? false : value))
        )
      } else {
        setDeployingLambdaLoader((prevArray) =>
          prevArray.map((value, i) => (masterLambdaID && master_lambdas[i]?.id === masterLambdaID ? false : value))
        )
      }
      setMasterLambdaID(null);
      setLambdaArn(null);
      setRoleArn(null)
    }
    setDeployDetailsModalOpen(!deployDetailsModalOpen);
  }

  function toggleScriptModal() {
    if (scriptModalOpen) {
      setSelectedScript(null);
    }
    setScriptModalOpen(!scriptModalOpen);
  }

  return (
    <>
      <div className="d-flex gap-1">
        <Breadcrumbs
          title={"Lambda Functions"}
          breadcrumbItem={"Lambda Functions"}
        />
        <span role="button" className="align-items-center" id='lambdaTooltips'>
          <i className="bx bx-help-circle font-size-16 text-white"/>
        </span>
        <DocsTooltip
          placement={'right'}
          isOpen={lambdaToolTip}
          toggle={() => {
            setLambdaToolTip(!lambdaToolTip)
          }}
          targetId={'lambdaTooltips'}
          multipleLinks={[
            {
              linkText: 'Manual Lambda Deployment',
              link: 'https://docs.temperstack.com/temperstack/'
            },
            {
              linkText: 'One-Click Deployment',
              link: 'https://docs.temperstack.com/temperstack/'
            }
          ]}
        />
      </div>
      <div className="d-flex justify-content-between align-items-center mt-3 mb-2">
        <h5>Available Lambda Functions & Runbook Scripts</h5>
        <button
          color="primary"
          className="btn btn-primary"
          onClick={toggleScriptModal}
        >
          <i className="bx bx-plus me-1"></i> Add Script
        </button>
      </div>
      <AddRunbookScriptModal
        isOpen={scriptModalOpen}
        toggle={toggleScriptModal}
        scriptDetails={selectedScript}
        isViewMode={!!selectedScript}
      />
      <div className="card-transparent">
        <TableHeaders headers={masterLambdaHeaders} className="mt-1"/>
        <div>
          {masterLambdaID && (
            <DeployDetailsModal
              toggle={toggleDeployDetailsModal}
              isOpen={deployDetailsModalOpen}
              masterLambdaId={masterLambdaID}
              setSubmitted={setDeployDetailsModalSubmitted}
              defaultDeploymentOption={deployDetailsModalForUpdate ? 1 : 0}
              roleArn={roleArn}
              lambdaArn={lambdaArn}
            />
          )}
          {master_lambdas?.map((master_lambda, index) => (
            <CardItem
              key={`lambda-${master_lambda.id}`}
              className="card-item"
            >
              <Row>
                <Col md={masterLambdaHeaders[0].width} lg={masterLambdaHeaders[0].width}
                     id={`lambda_code_${master_lambda?.id}`}
                     className="align-items-center justify-content-center py-1 d-flex">
                  <Popover
                    placement="top"
                    isOpen={masterLambdaLinkPopover[master_lambda.id] || false}
                    target={`lambda_code_${master_lambda?.id}`}
                  >
                    <PopoverBody>{convertS3UriToUrl(master_lambda.script_url)}</PopoverBody>
                  </Popover>
                  <Link
                    to={convertS3UriToUrl(master_lambda.script_url)}
                    className="link-primary text-center"
                    target="_blank"
                    rel="noreferrer"
                    onMouseEnter={() => toggleScriptUrlTooltip(master_lambda.id, true)}
                    onMouseLeave={() => toggleScriptUrlTooltip(master_lambda.id, false)}
                  >
                    {master_lambda?.lambda_function_name}
                    <i className='bx bx-link-external'></i>
                  </Link>
                </Col>
                <Col md={masterLambdaHeaders[1].width} lg={masterLambdaHeaders[1].width}
                     className="align-items-center justify-content-center py-1 d-flex">
                  {master_lambda?.description}
                </Col>
                <Col md={masterLambdaHeaders[2].width} lg={masterLambdaHeaders[2].width}
                     className="align-items-center justify-content-center py-1 d-flex">
                  {deployingLambdaLoader[index] ?
                    <Spinner className="ms-2" color="primary"/> :
                    <button
                      color="primary"
                      className="btn btn-primary"
                      type="submit"
                      onClick={(e) => {
                        setMasterLambdaID(master_lambda.id)
                        setDeployDetailsModalForUpdate(false)
                        toggleDeployDetailsModal()
                        setDeployingLambdaLoader((prevArray) =>
                          prevArray.map((value, i) => (i === index ? true : value))
                        );
                      }}>
                      Deploy
                    </button>
                  }
                </Col>
              </Row>
            </CardItem>
          ))}
          {runbook_scripts && runbook_scripts.length > 0 ? (
            runbook_scripts?.map((script) => (
              <CardItem
                key={`script-${script.id}`}
                className="card-item"
              >
                <Row className="mx-1 d-flex py-1">
                  <Col md={masterLambdaHeaders[0].width} lg={masterLambdaHeaders[0].width}
                       id={`script_name_${script?.id}`}
                       className="align-items-center justify-content-center py-1 d-flex">
                    <a
                      href="#"
                      className="link-primary text-center"
                      onClick={(e) => {
                        e.preventDefault();
                        setSelectedScript(script);
                        toggleScriptModal();
                      }}
                    >
                      {script.name}
                    </a>
                  </Col>
                  <Col
                    md={masterLambdaHeaders[1].width}
                    lg={masterLambdaHeaders[1].width}
                    className="align-items-center justify-content-center py-1 d-flex"
                  >
                    {script.description ? (
                      script.description.length > 100
                        ? `${script.description.substring(0, 100)}...`
                        : script.description
                    ) : ""}
                  </Col>
                  <Col
                    md={masterLambdaHeaders[2].width}
                    lg={masterLambdaHeaders[2].width}
                    className="align-items-center justify-content-center py-1 d-flex"
                  >
                    <button
                      color="primary"
                      className="btn btn-primary"
                      onClick={() => handleDeployScript(script.id)}
                    >
                      Deploy
                    </button>
                  </Col>
                </Row>
              </CardItem>
            ))
          ) : (
            runbook_scripts && runbook_scripts.length === 0 && master_lambdas.length === 0 && (
              <CardItem className="card-item">
                <Row className="mx-1 d-flex py-3">
                  <Col className="text-center">
                    No lambda functions or runbook scripts found.
                  </Col>
                </Row>
              </CardItem>
            )
          )}
        </div>
      </div>
      <div className="mt-3 mb-2">
        <h5>Deployed Lambda Functions</h5>
        <Accordion open={openAccount} toggle={toggleAccordion}>
          {Object.entries(deployed_lambdas_grouped).map(([accountName, lambdas], index) => (
            <AccordionItem key={index}>
              <AccordionHeader targetId={accountName}>
                <div className="d-flex justify-content-between align-items-center w-100">
                  <span className="me-3">{accountName}</span>
                  <Link to={`/daemon-lambdas/map?monitoring_system_id=${lambdas[0]?.monitoring_system?.id}`} className="btn btn-primary btn-sm">
                    Map Alerts to Lambda Functions
                  </Link>
                </div>
              </AccordionHeader>
              <AccordionBody accordionId={accountName}>
                {lambdas.length === 0 ? (
                  <div>No lambdas deployed.</div>
                ) : (
                  <div className="card-transparent">
                    <TableHeaders headers={deployedLambdaHeaders} className="mt-1"/>
                    {lambdas.map((deployed_lambda, subIndex) => (
                      <CardItem key={subIndex} className="card-item">
                        <Row className="mx-1 py-1">
                          <Col
                            md={deployedLambdaHeaders[0].width}
                            lg={deployedLambdaHeaders[0].width}
                            className="align-items-center justify-content-center py-1 d-flex"
                          >
                            {deployed_lambda?.name}
                          </Col>
                          <Col
                            id={`deployed_lambda_arn_${deployed_lambda.id}`}
                            md={deployedLambdaHeaders[1].width}
                            lg={deployedLambdaHeaders[1].width}
                            className="py-1 d-flex align-items-center"
                            style={{
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              direction: 'ltr',
                              textAlign: 'start',
                            }}
                          >
                            <Popover
                              placement="top"
                              isOpen={deployedLambdaArnPopover[deployed_lambda.id] || false}
                              target={`deployed_lambda_arn_${deployed_lambda.id}`}
                            >
                              <PopoverBody>{deployed_lambda?.lambda_arn}</PopoverBody>
                            </Popover>
                            <span
                              onMouseEnter={() =>
                                toggleDeployedLambdaArnTooltip(deployed_lambda.id, true)
                              }
                              onMouseLeave={() =>
                                toggleDeployedLambdaArnTooltip(deployed_lambda.id, false)
                              }
                              style={{
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                display: 'block',
                              }}
                            >
                          {deployed_lambda?.lambda_arn}
                        </span>
                            <i
                              className="bx bx-copy-alt mx-1 my-2"
                              style={{
                                textAlign: 'center',
                                verticalAlign: 'middle',
                                fontSize: 'small',
                                cursor: 'pointer',
                              }}
                              onClick={() => {
                                copyToClipboard(deployed_lambda?.lambda_arn);
                              }}
                            ></i>
                          </Col>
                          <Col
                            id={`deployed_lambda_role_arn_${deployed_lambda.id}`}
                            md={deployedLambdaHeaders[2].width}
                            lg={deployedLambdaHeaders[2].width}
                            className="py-1 d-flex align-content-between"
                            style={{
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              direction: 'ltr',
                              textAlign: 'start',
                            }}
                          >
                            <Popover
                              placement="top"
                              isOpen={deployedLambdaRoleArnPopover[deployed_lambda.id] || false}
                              target={`deployed_lambda_role_arn_${deployed_lambda.id}`}
                            >
                              <PopoverBody>{deployed_lambda?.role_arn}</PopoverBody>
                            </Popover>
                            <span
                              onMouseEnter={() =>
                                toggleDeployedLambdaRoleArnTooltip(deployed_lambda.id, true)
                              }
                              onMouseLeave={() =>
                                toggleDeployedLambdaRoleArnTooltip(deployed_lambda.id, false)
                              }
                              style={{
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                display: 'block',
                              }}
                            >
                          {deployed_lambda?.role_arn}
                        </span>
                            <i
                              className="bx bx-copy-alt mx-1 my-2"
                              style={{
                                textAlign: 'center',
                                verticalAlign: 'middle',
                                fontSize: 'small',
                                cursor: 'pointer',
                              }}
                              onClick={() => {
                                copyToClipboard(deployed_lambda?.role_arn);
                              }}
                            ></i>
                          </Col>
                          <Col
                            md={deployedLambdaHeaders[3].width}
                            lg={deployedLambdaHeaders[3].width}
                            className="py-1 d-flex align-items-center justify-content-evenly"
                          >
                            {updatingLambdaLoader[subIndex] ? (
                              <Spinner className="ms-2" color="primary"/>
                            ) : (
                              <i
                                className="mdi mdi-pencil text-success"
                                style={{
                                  cursor: 'pointer',
                                }}
                                onClick={() => {
                                  setLambdaArn(deployed_lambda?.lambda_arn);
                                  setRoleArn(deployed_lambda?.role_arn);
                                  setMasterLambdaID(deployed_lambda?.master_lambda_id);
                                  setDeployDetailsModalForUpdate(true);
                                  toggleDeployDetailsModal();
                                  setUpdatingLambdaLoader((prevArray) =>
                                    prevArray.map((value, i) =>
                                      i === subIndex ? true : value
                                    )
                                  );
                                }}
                              ></i>
                            )}
                          </Col>
                        </Row>
                      </CardItem>
                    ))}
                  </div>
                )}
              </AccordionBody>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </>
  );
};

export default LambdaDetails;